import React, { useEffect, useMemo, useState } from "react";

import {
  Settings2,
  ChevronLeft,
  User2,
  SlidersHorizontal,
  MessageSquare,
  CheckCircle2,
  Banknote,
  Globe,
  Sparkles,
} from "lucide-react";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import useClients from "../Services/ClientServices/ClientConfig/useClientConfig";

import ClientDetailsTab from "./ClientConfigTabs/ClientDetailsTab";
import ClientWhatsAppTab from "./ClientConfigTabs/ClientWhatsAppTab";
import ClientOnboardingTab from "./ClientConfigTabs/ClientOnboardingTab";
import ClientBeneficiaryTab from "./ClientConfigTabs/ClientBeneficiaryTab";
import ClientConfigTab from "./ClientConfigTabs/ClientConfigTab";
import ClientSettingsTab from "./ClientConfigTabs/ClientSettingsTab";
import ClientMoreTab from "./ClientConfigTabs/ClientMoreTab";

export default function ClientConfiguration() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isDeveloperRoute = location.pathname.includes("/admin/developer");
  const isClientsRoute = location.pathname.includes("/admin/clients");

  const {
    busy,
    activate,
    deactivate,
    subscribe,
    register,
    fetchWhatsappCredentials,
    updateWhatsappCredentials,
  } = useClients();

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        icon: User2,
        showInDeveloper: true,
        showInClients: true,
      },
      {
        label: "Config",
        icon: SlidersHorizontal,
        showInDeveloper: true,
        showInClients: true,
      },

      {
        label: "WhatsApp",
        icon: MessageSquare,
        showInDeveloper: true,
        showInClients: true,
      },
      {
        label: "Onboarding",
        icon: CheckCircle2,
        showInDeveloper: true,
        showInClients: false,
      },
      {
        label: "Beneficiary",
        icon: Banknote,
        showInDeveloper: true,
        showInClients: true,
      },
      {
        label: "Website Settings",
        icon: Globe,
        showInDeveloper: true,
        showInClients: true,
      },
      {
        label: "More",
        icon: Sparkles,
        showInDeveloper: true,
        showInClients: false,
      },
    ],
    []
  );

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (isDeveloperRoute) return tab.showInDeveloper;
      if (isClientsRoute) return tab.showInClients;

      return tab.showInClients;
    });
  }, [tabs, isDeveloperRoute, isClientsRoute]);

  const [activeTab, setActiveTab] = useState("Details");
  const [configData, setConfigData] = useState(null);

  useEffect(() => {
    const finalToken = token || location?.state?.token;

    const fallbackPath = isDeveloperRoute ? "/admin/developer" : "/admin/clients";

    if (!finalToken) {
      navigate(fallbackPath, { replace: true });
      return;
    }

    const raw = sessionStorage.getItem(`client_config_${finalToken}`);

    if (!raw) {
      navigate(fallbackPath, { replace: true });
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      const TEN_MINUTES = 10 * 60 * 1000;
      const isExpired =
        Date.now() - Number(parsed.createdAt || 0) > TEN_MINUTES;

      if (isExpired) {
        sessionStorage.removeItem(`client_config_${finalToken}`);
        navigate(fallbackPath, { replace: true });
        return;
      }

      setConfigData(parsed);
    } catch (error) {
      navigate(fallbackPath, { replace: true });
    }
  }, [
    token,
    location?.state?.token,
    navigate,
    isDeveloperRoute,
  ]);

  useEffect(() => {
    const activeTabExists = visibleTabs.some(
      (tab) => tab.label === activeTab
    );

    if (!activeTabExists) {
      setActiveTab(visibleTabs[0]?.label || "Details");
    }
  }, [visibleTabs, activeTab]);

  if (!configData) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f4f8fb] text-sm text-gray-500">
        Verifying client access...
      </div>
    );
  }

  const selectedClient = configData.client;
  const organizationName = configData.organizationName;
  const id = configData.clientId;
  const currentGateway =
    configData.paymentGateway || selectedClient?.payment_gateway || "";

  return (
    <div className="min-h-screen bg-[#f4f8fb]">
      <Toaster position="top-right" />

      {/* TOP HEADER */}
      <div className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="flex flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between lg:px-6">
          {/* LEFT */}
          <div className="flex items-center gap-4">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#01C37D]/10">
              <Settings2
                className="text-[#01C37D]"
                size={28}
              />
            </div>

            <div>
              <h1 className="text-xl text-gray-900">
                {organizationName || "Client Configuration"}
              </h1>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <div className="rounded-full bg-[#01C37D]/10 px-3 py-1 text-xs font-bold text-[#01C37D]">
                  CLIENT ID : {id}
                </div>

                <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                  ACTIVE CLIENT
                </div>

                <div
                  className={`rounded-full border px-3 py-1 text-xs font-bold ${isDeveloperRoute
                    ? "border-purple-200 bg-purple-50 text-purple-700"
                    : "border-gray-200 bg-gray-50 text-gray-700"
                    }`}
                >
                  {isDeveloperRoute ? "DEVELOPER VIEW" : "CLIENT VIEW"}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <button
            onClick={() => navigate(-1)}
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm"
          >
            <ChevronLeft size={16} />
            Back
          </button>
        </div>

        {/* TABS */}
        <div className="overflow-x-auto px-4 pb-4 lg:px-6">
          <div className="flex min-w-max gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
            {visibleTabs.map((tab) => {
              const active = activeTab === tab.label;
              const Icon = tab.icon;

              return (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(tab.label)}
                  className={`
                    flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all
                    ${active
                      ? "bg-[#01C37D] text-white shadow-lg shadow-[#01C37D]/20"
                      : "text-gray-600 hover:bg-gray-100"
                    }
                  `}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* PAGE CONTENT */}
      <div className="px-4 py-6 lg:px-6">
        {activeTab === "Details" && (
          <ClientDetailsTab
            clientName={organizationName}
            clientId={id}
            selectedClient={selectedClient}
            currentGateway={currentGateway}
            busy={busy}
            onSubscribe={subscribe}
            onRegister={register}
            onActivate={activate}
            onDeactivate={deactivate}
          />
        )}

        {activeTab === "Config" && (
          <ClientConfigTab
            clientName={organizationName}
            clientId={id}
            selectedClient={selectedClient}
          />
        )}

        {activeTab === "WhatsApp" && (
          <ClientWhatsAppTab
            clientName={organizationName}
            clientId={id}
            busy={busy}
            onFetchWA={fetchWhatsappCredentials}
            onSaveWA={updateWhatsappCredentials}
          />
        )}

        {activeTab === "Onboarding" && (
          <ClientOnboardingTab
            clientName={organizationName}
            clientId={id}
          />
        )}

        {activeTab === "Beneficiary" && (
          <ClientBeneficiaryTab
            clientName={organizationName}
            clientId={id}
          />
        )}

        {activeTab === "Website Settings" && (
          <ClientSettingsTab
            clientName={organizationName}
            clientId={id}
          />
        )}

        {activeTab === "More" && isDeveloperRoute && (
          <ClientMoreTab
            clientName={organizationName}
            clientId={id}
          />
        )}
      </div>
    </div>
  );
}